import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const STARTERS = {
  copilot: [
    "What's driving my abandons this week?",
    "How can I reduce my AI costs by 20%?",
    "Which features are underused?",
    "Who are my at-risk users?",
  ],
  cost: [
    "Give me a cost breakdown by feature",
    "What's my projected monthly spend?",
    "Where am I wasting tokens?",
    "How does my cost compare to last week?",
  ],
  cs: [
    "Which users show churn signals?",
    "What's my booking conversion rate?",
    "Why is support volume high this week?",
    "Who needs a check-in?",
  ],
  product: [
    "What should we build next based on user behaviour?",
    "Where do users drop off in the booking flow?",
    "Which AI feature gets the most usage?",
    "What's causing low booking conversion?",
  ],
  admin: [
    "Run a full status check",
    "Were the recent brain decisions correct?",
    "Update the brain prompt to be more aggressive on churn",
    "What thresholds should I adjust?",
  ],
};

const TYPE_LABELS = {
  copilot: { label: '⬡ Copilot', color: '#00D9FF' },
  cost:    { label: '💰 Cost Analyst', color: '#ffd93d' },
  cs:      { label: '🤝 Customer Success', color: '#00ff88' },
  product: { label: '🧠 Product Intelligence', color: '#a855f7' },
  admin:   { label: '🔧 Admin Intelligence', color: '#f97316' },
};

export default function Copilot({ tenantId, threadType = 'copilot', threadId, threadTitle, onClose, onNewThread }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ctxSnapshot, setCtxSnapshot] = useState('');
  const [activeThreadId, setActiveThreadId] = useState(threadId || null);
  const bottomRef = useRef(null);
  const meta = TYPE_LABELS[threadType] || TYPE_LABELS.copilot;

  // Load existing thread messages if threadId provided
  useEffect(() => {
    if (threadId && tenantId) {
      axios.get(`${API}/tenants/${tenantId}/threads/${threadId}/messages`)
        .then(r => {
          const msgs = r.data.map(m => ({ role: m.role, content: m.content }));
          setMessages(msgs.length ? msgs : [{ role: 'assistant', content: getWelcome(threadType) }]);
        })
        .catch(() => setMessages([{ role: 'assistant', content: getWelcome(threadType) }]));
    } else {
      setMessages([{ role: 'assistant', content: getWelcome(threadType) }]);
    }
    // Reset thread tracking when threadId prop changes
    setActiveThreadId(threadId || null);
  }, [threadId, tenantId, threadType]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  function getWelcome(type) {
    const w = {
      copilot: "Hi! I'm your Anise assistant. I have your live account data. Ask me anything about costs, usage, or product decisions.",
      cost:    "I'm your Cost Analyst. I can break down your AI spend, find waste, and project future costs. What would you like to analyse?",
      cs:      "I'm your Customer Success agent. I monitor churn signals, support volume, and booking conversion. Who should we focus on?",
      product: "I'm your Product Intelligence agent. I analyse user behaviour, feature adoption, and drop-off points. What insight do you need?",
      admin:   "I'm your Admin Intelligence assistant. I'll walk you through what the brain has decided, ask if you agree, and help you update the brain prompt to close the loop. Ready?",
    };
    return w[type] || w.copilot;
  }

  const send = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;
    setInput('');

    // Create thread on first message if no threadId
    let tid = activeThreadId;
    if (!tid) {
      try {
        const r = await axios.post(`${API}/tenants/${tenantId}/threads`, {
          thread_type: threadType,
          title: userMsg.slice(0, 60),
        });
        tid = r.data.id;
        setActiveThreadId(tid);
        if (onNewThread) onNewThread(tid, userMsg.slice(0, 60));
      } catch (e) { /* continue without thread persistence */ }
    }

    const next = [...messages, { role: 'user', content: userMsg }];
    setMessages(next);
    setLoading(true);

    try {
      // axios.defaults.headers.common['Authorization'] is set by AuthContext on login
      const res = await axios.post(`${API}/copilot`, {
        messages: next,
        thread_type: threadType,
        thread_id: tid,
      });

      setCtxSnapshot(res.data.context_snapshot || '');
      setMessages([...next, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages([...next, { role: 'assistant', content: '⚠ Error — please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const starters = STARTERS[threadType] || STARTERS.copilot;

  return (
    <div style={{
      position: 'fixed', right: 0, bottom: 0, top: 0, width: 420,
      background: 'rgba(8,18,36,0.99)', borderLeft: '1px solid rgba(0,217,255,0.12)',
      display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.6)',
      zIndex: 100,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: meta.color }}>{meta.label}</div>
          {(threadTitle || activeThreadId) && (
            <div style={{ fontSize: 11, color: '#475569', marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {threadTitle || 'Current conversation'}
            </div>
          )}
          {ctxSnapshot && <div style={{ fontSize: 10, color: '#334155', marginTop: 2 }}>{ctxSnapshot}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button
            title="New conversation"
            onClick={() => {
              setMessages([{ role: 'assistant', content: getWelcome(threadType) }]);
              setActiveThreadId(null);
              setCtxSnapshot('');
            }}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)',
              color: '#475569', cursor: 'pointer', fontSize: 11, borderRadius: 6,
              padding: '3px 8px', lineHeight: 1.4 }}>
            + New
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            color: '#475569', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}
            data-testid="copilot-close">×</button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              data-role={m.role}
              style={{
              maxWidth: '88%', padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.65,
              background: m.role === 'user' ? `linear-gradient(135deg,${meta.color},${meta.color}99)` : 'rgba(255,255,255,0.05)',
              color: m.role === 'user' ? '#0a0f1e' : '#e2e8f0',
              border: m.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.07)',
              borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              whiteSpace: 'pre-wrap',
            }}>{m.content}</div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex' }}>
            <div style={{ padding: '10px 16px', borderRadius: '12px 12px 12px 2px',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)',
              fontSize: 18, letterSpacing: 4, color: meta.color }}>···</div>
          </div>
        )}
        {messages.length === 1 && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 6 }}>
            <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>Try asking</div>
            {starters.map((s, i) => (
              <button key={i} onClick={() => send(s)} style={{
                textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)', border: `1px solid ${meta.color}33`,
                color: '#94a3b8', fontSize: 12,
              }}>{s}</button>
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            data-testid="copilot-input"
            style={{ flex: 1, padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
              color: '#fff', fontSize: 13, outline: 'none' }}
            placeholder={`Ask your ${meta.label.split(' ').slice(1).join(' ') || 'assistant'}…`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            disabled={loading}
          />
          <button onClick={() => send()} disabled={loading || !input.trim()} style={{
            padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: meta.color, color: '#0a0f1e', fontWeight: 700, fontSize: 14,
            opacity: loading || !input.trim() ? 0.4 : 1,
          }}>↑</button>
        </div>
      </div>
    </div>
  );
}
